import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Chip,
  Stack,
  IconButton,
  Paper,
  useMediaQuery,
} from '@mui/material';
import {
  Speed,
  Security,
  SupportAgent,
  TrendingUp,
  Public,
  ArrowForward,
  PlayCircle,
  CheckCircle,
  East,
  Router,
  CellTower,
  Storage,
  Wifi,
  Cloud,
  Analytics,
  NetworkCell,
  Shield,
  HeadsetMic,
  Bolt,
  Language,
  Business,
  Handshake,
  People,
  ChevronLeft,
  ChevronRight,
  Visibility,
  Flag,
  Star,
} from '@mui/icons-material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentFlagIndex, setCurrentFlagIndex] = useState(0);

  const features = [
    { icon: <Bolt />, title: 'Lightning Fast', desc: 'Up to 100Gbps connectivity', color: '#e60108', delay: 0.3 },
    { icon: <Shield />, title: 'Bank-Level Security', desc: 'Enterprise-grade protection', color: '#fefefe', delay: 0.4 },
    { icon: <HeadsetMic />, title: '24/7 Support', desc: 'Global support team', color: '#e60108', delay: 0.5 },
    { icon: <TrendingUp />, title: '99.999% Uptime', desc: 'Industry leading reliability', color: '#fefefe', delay: 0.6 },
  ];

  const countryFlags = [
    { name: 'South Africa', image: '/images/Flag of South Africa.png' },
    { name: 'Eswatini', image: '/images/country flag sz.png' },
    { name: 'Namibia', image: '/images/Namibia Flag.jpg' },
    { name: 'Botswana', image: '/images/Flags of Botswana.png' },
    { name: 'Zimbabwe', image: '/images/Zimbabwe Flag.png' },
    { name: 'Mozambique', image: '/images/Flag of Mozambique.png' },
    { name: 'Lesotho', image: '/images/Lesotho Large.png' },
  ];

  const services = [
    {
      title: 'Telecom Towers',
      desc: '100+ towers with 5G-ready infrastructure',
      icon: <CellTower />,
      color: '#191919',
      image: '/images/Telecom Tower Infrastructure.jpg',
      stats: '98% Coverage',
    },
    {
      title: 'Data Centers',
      desc: 'Secure data centers worldwide',
      icon: <Storage />,
      color: '#e60108',
      image: '/images/DataCenters.jpg',
      stats: 'Tier III+ Certified',
    },
    {
      title: 'Internet Solutions',
      desc: 'High-speed internet for businesses of all sizes',
      icon: <NetworkCell />,
      color: '#a0a0a0',
      image: '/images/Internet Solutions.jpg',
      stats: 'Up to 100Gbps',
    },
  ];

  const partners = [
    { name: 'Worldwide Capital', icon: <Business />, industry: 'Finance', color: '#e60108' },
    { name: 'Worldwide Distribution', icon: <Handshake />, industry: 'Logistics', color: '#191919' },
    { name: 'Worldwide Chat', icon: <People />, industry: 'Communications', color: '#e60108' },
  ];

  const coreValues = [
    {
      title: 'Innovation',
      desc: 'Embracing technology to solve challenges',
      icon: <Bolt />,
      color: '#e60108',
    },
    {
      title: 'Integrity',
      desc: 'Building trust through transparency',
      icon: <Shield />,
      color: '#191919',
    },
    {
      title: 'Excellence',
      desc: 'Delivering quality in everything we do',
      icon: <Star />,
      color: '#e60108',
    },
    {
      title: 'Community',
      desc: 'Empowering the communities we serve',
      icon: <People />,
      color: '#191919',
    },
  ];

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFlagIndex((prev) => (prev + 1) % countryFlags.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [countryFlags.length]);

  const handlePrev = () => {
    setCurrentFlagIndex((prev) => (prev - 1 + countryFlags.length) % countryFlags.length);
  };

  const handleNext = () => {
    setCurrentFlagIndex((prev) => (prev + 1) % countryFlags.length);
  };

  // Get visible flags for slider
  const getVisibleFlags = () => {
    const visibleCount = isMobile ? 1 : 5;
    const flags = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentFlagIndex + i) % countryFlags.length;
      flags.push(countryFlags[index]);
    }
    return flags;
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Background Image with Parallax Effect */}
        <motion.div
          style={{
            position: 'absolute',
            width: '100%',
            height: '110%',
            top: '-5%',
            left: 0,
            scale: scale,
          }}
        >
          <Box
            component="img"
            src="/images/telecommmunication.jpg"
            alt="Telecommunication Network"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </motion.div>

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 50%, rgba(230,1,8,0.2) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Global Leader in Communication"
              icon={<Language sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                backdropFilter: 'blur(10px)',
                fontWeight: 700,
                letterSpacing: '0.5px',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />

            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '5rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.4)',
              }}
            >
              Connecting the World,
              <br />
              <Box
                component="span"
                sx={{
                  color: '#e60108',
                  textShadow: '0 0 30px rgba(230,1,8,0.5)',
                  display: 'inline-block',
                }}
              >
                Bridging Continents
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#fefefe',
                mb: 4,
                maxWidth: 650,
                fontSize: '1.25rem',
                lineHeight: 1.6,
                textShadow: '0 1px 10px rgba(0,0,0,0.3)',
                opacity: 0.95,
              }}
            >
              Global communication solutions that power businesses across countries with cutting-edge infrastructure and 5G-ready technology.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/contact')}
                  endIcon={<East />}
                  sx={{
                    bgcolor: '#e60108',
                    color: '#fefefe',
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: '#b80006',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(230,1,8,0.4)',
                    },
                  }}
                >
                  Get Started
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/services')}
                  startIcon={<PlayCircle />}
                  sx={{
                    color: '#fefefe',
                    borderColor: '#fefefe',
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderRadius: 3,
                    '&:hover': {
                      borderColor: '#e60108',
                      bgcolor: 'rgba(230,1,8,0.15)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Explore Services
                </Button>
              </motion.div>
            </Stack>

            {/* Feature Pills - Centered */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              justifyContent="center"
              sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(254,254,254,0.2)' }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: feature.delay }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        transform: 'translateX(5px)',
                      },
                    }}
                  >
                    <Box sx={{ color: feature.color, bgcolor: 'rgba(254,254,254,0.15)', p: 1, borderRadius: 2 }}>
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="#fefefe">
                        {feature.title}
                      </Typography>
                      <Typography variant="caption" color="rgba(254,254,254,0.8)">
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Stack>
                </motion.div>
              ))}
            </Stack>
          </motion.div>
        </Container>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: 'absolute',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <IconButton
            sx={{
              color: '#fefefe',
              bgcolor: 'rgba(230,1,8,0.3)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: '#e60108',
                transform: 'scale(1.1)',
              },
            }}
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <Box component="span" sx={{ fontSize: '1.5rem' }}>↓</Box>
          </IconButton>
        </motion.div>
      </Box>

      {/* Country Flags Slider Section */}
      <Box sx={{ py: 4, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 0,
                  zIndex: 2,
                  bgcolor: '#fefefe',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: '#fefefe', transform: 'scale(1.05)' },
                }}
              >
                <ChevronLeft />
              </IconButton>

              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                  px: 5,
                  py: 2,
                }}
              >
                {getVisibleFlags().map((flag, index) => (
                  <motion.div
                    key={flag.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: isMobile ? '80px' : '120px',
                    }}
                  >
                    <Box
                      component="img"
                      src={flag.image}
                      alt={flag.name}
                      sx={{
                        width: isMobile ? 60 : 100,
                        height: isMobile ? 40 : 67,
                        objectFit: 'cover',
                        borderRadius: 1,
                        boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        color: '#191919',
                        fontSize: isMobile ? '0.6rem' : '0.75rem',
                        textAlign: 'center',
                      }}
                    >
                      {flag.name}
                    </Typography>
                  </motion.div>
                ))}
              </Box>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 0,
                  zIndex: 2,
                  bgcolor: '#fefefe',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: '#fefefe', transform: 'scale(1.05)' },
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Dots indicator */}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
              {countryFlags.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentFlagIndex(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: index === currentFlagIndex ? '#e60108' : '#d0d0d0',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: index === currentFlagIndex ? '#e60108' : '#a0a0a0',
                      transform: 'scale(1.2)',
                    },
                  }}
                />
              ))}
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* Vision & Mission Section */}
      <Box sx={{ py: 12, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Purpose"
              icon={<Star sx={{ fontSize: 16 }} />}
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
                '& .MuiChip-icon': { color: '#e60108' },
              }}
            />
            <Typography
              variant="h2"
              sx={{ mb: 2, fontWeight: 800, color: '#191919' }}
            >
              Vision &amp; Mission
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}
            >
              Driving the future of global communication
            </Typography>
          </motion.div>

          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
            sx={{ mb: 10 }}
          >
            {/* Vision Card */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 5 },
                    width: '100%',
                    minHeight: { xs: 'auto', md: 380 },
                    borderRadius: 4,
                    border: '2px solid #e6010820',
                    bgcolor: '#fefefe',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#e60108',
                      boxShadow: '0 20px 40px rgba(230,1,8,0.12)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 180,
                      height: 180,
                      background:
                        'radial-gradient(circle at top right, #e6010815 0%, transparent 70%)',
                      pointerEvents: 'none',
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: '#e6010815',
                      color: '#e60108',
                      mb: 3,
                      mx: 'auto',
                    }}
                  >
                    <Visibility sx={{ fontSize: 36 }} />
                  </Avatar>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      fontWeight: 800,
                      color: '#191919',
                      textAlign: 'center',
                    }}
                  >
                    Our Vision
                  </Typography>
                  <Box
                    sx={{
                      width: 50,
                      height: 4,
                      bgcolor: '#e60108',
                      borderRadius: 2,
                      mx: 'auto',
                      mb: 3,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      textAlign: 'center',
                      maxWidth: 420,
                      mx: 'auto',
                    }}
                  >
                    To be the leading telecommunications provider in Africa,
                    connecting communities and businesses across borders with
                    innovative, reliable, and affordable communication solutions
                    that empower economic growth and digital inclusion for all.
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            {/* Mission Card */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 5 },
                    width: '100%',
                    minHeight: { xs: 'auto', md: 380 },
                    borderRadius: 4,
                    border: '2px solid #19191920',
                    bgcolor: '#fefefe',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#191919',
                      boxShadow: '0 20px 40px rgba(25,25,25,0.12)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 180,
                      height: 180,
                      background:
                        'radial-gradient(circle at top right, #19191915 0%, transparent 70%)',
                      pointerEvents: 'none',
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: '#19191915',
                      color: '#191919',
                      mb: 3,
                      mx: 'auto',
                    }}
                  >
                    <Flag sx={{ fontSize: 36 }} />
                  </Avatar>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      fontWeight: 800,
                      color: '#191919',
                      textAlign: 'center',
                    }}
                  >
                    Our Mission
                  </Typography>
                  <Box
                    sx={{
                      width: 50,
                      height: 4,
                      bgcolor: '#191919',
                      borderRadius: 2,
                      mx: 'auto',
                      mb: 3,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      textAlign: 'center',
                      maxWidth: 420,
                      mx: 'auto',
                    }}
                  >
                    To deliver world-class telecommunications infrastructure and
                    services that bridge the digital divide across Southern Africa.
                    We are committed to providing cutting-edge technology,
                    exceptional customer service, and sustainable solutions that
                    create lasting value for our customers, partners, and
                    communities.
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>

          {/* Core Values Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 5,
                  fontWeight: 700,
                  color: '#191919',
                  textAlign: 'center',
                }}
              >
                Our Core Values
              </Typography>
              <Grid
                container
                spacing={3}
                justifyContent="center"
                alignItems="stretch"
              >
                {coreValues.map((value, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={index}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <motion.div
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.3 }}
                      style={{ width: '100%', display: 'flex' }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          width: '100%',
                          borderRadius: 3,
                          bgcolor: '#f8f8f8',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'center',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: '#fefefe',
                            boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: value.color + '15',
                            color: value.color,
                            mb: 2,
                            mx: 'auto',
                          }}
                        >
                          {value.icon}
                        </Avatar>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          sx={{ color: '#191919', mb: 1, textAlign: 'center' }}
                        >
                          {value.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#a0a0a0',
                            textAlign: 'center',
                            lineHeight: 1.6,
                            display: 'block',
                          }}
                        >
                          {value.desc}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section with Single Future Goal Note */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          {/* Single Future Goal Note - Mentioned Once */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                bgcolor: '#f8f8f8',
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 700,
                mx: 'auto',
                border: '1px solid #e6010820',
              }}
            >
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Services Overview */}
      <Box sx={{ py: 12, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Services"
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography
              variant="h2"
              sx={{ mb: 2, fontWeight: 800, color: '#191919' }}
            >
              Our Core Services
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}
            >
              Comprehensive communication solutions tailored to your needs
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      borderRadius: 4,
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      },
                    }}
                    onClick={() => navigate('/services')}
                  >
                    <Box
                      sx={{
                        height: 200,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${service.color}60 0%, transparent 100%)`,
                        }}
                      />
                      <Chip
                        label={service.stats}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: 16,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: '#fefefe',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: service.color + '15',
                          color: service.color,
                          mb: 2,
                          mx: 'auto',
                        }}
                      >
                        {service.icon}
                      </Avatar>
                      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#191919' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', lineHeight: 1.6 }}>
                        {service.desc}
                      </Typography>
                      <Button
                        endIcon={<ArrowForward />}
                        sx={{
                          mt: 2,
                          color: service.color,
                          p: 0,
                          '&:hover': { transform: 'translateX(5px)', backgroundColor: 'transparent' },
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* View All Services Button - Centered */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/services')}
                endIcon={<ArrowForward />}
                sx={{
                  borderColor: '#e60108',
                  color: '#e60108',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: '#b80006',
                    bgcolor: '#e6010810',
                  },
                }}
              >
                View All Services
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box sx={{ py: 12, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip
                  label="Why Choose Us"
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Setting the Standard for <br />
                  <Box component="span" sx={{ color: '#e60108' }}>Global Communication</Box>
                </Typography>

                <Stack spacing={2} sx={{ mt: 4 }}>
                  {[
                    { text: 'uptime guaranteed', icon: <CheckCircle sx={{ fontSize: 20 }} /> },
                    { text: 'ISO 27001 certified security', icon: <Shield sx={{ fontSize: 20 }} /> },
                    { text: '24/7 multilingual support', icon: <HeadsetMic sx={{ fontSize: 20 }} /> },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: '#e6010808',
                            transform: 'translateX(8px)',
                          },
                        }}
                      >
                        <Box sx={{ color: '#e60108' }}>{item.icon}</Box>
                        <Typography variant="body1" sx={{ color: '#191919', fontWeight: 500 }}>{item.text}</Typography>
                      </Stack>
                    </motion.div>
                  ))}
                </Stack>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/about')}
                    sx={{
                      bgcolor: '#e60108',
                      px: 4,
                      py: 1.5,
                      mt: 4,
                      borderRadius: 3,
                      '&:hover': {
                        bgcolor: '#b80006',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Learn More About Us
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="/images/rural.png"
                  alt="Data Center"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Premium CTA Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)',
          color: '#fefefe',
          py: 10,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 800 }}>
              Ready to Transform Your Business?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
              Join businesses using our worldwide communication network
            </Typography>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/contact')}
                endIcon={<East />}
                sx={{
                  bgcolor: '#fefefe',
                  color: '#e60108',
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  '&:hover': {
                    bgcolor: '#fefefe',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  },
                }}
              >
                Get Started Today
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;